import React, { useEffect, useState } from 'react'
import { DatePicker, Input, Tooltip } from 'antd'
import moment from 'moment'
import { QuestionCircleIcon } from 'components/Icons'
import LifecycleInterventionInputStyled from './LifecycleInterventionInputStyled'

const LifecycleInterventionInput = ({
  inputKey = 'lifecycle-intervention-year',
  form = {},
  forceUpdate = () => {}
}) => {
  const DEFAULT_LIFECYCLE = 0
  const MAX_INTERVENTION_PERIOD_BEYOND_EOL = 30 // 30 years from EoL
  const DATE_FORMAT = 'YYYY-MM-DD'
  const HELP_TEXT = 'Year the asset is expected to require replacement based on its condition at survey. Default is calculated from the CIBSE lifecycle and install date. Amendments to this must be between the install date and up to 30 years beyond the CIBSE lifecycle. Include a comment to support your entry.'

  const installDate = form.getFieldValue('facets_install-date') || undefined
  const assetType = form.getFieldValue('assetType') || undefined
  const currentLifecycleInterventionYear = form.getFieldValue(inputKey) || undefined
  const currentLifecycleInterventionYearComment = form.getFieldValue(`${inputKey}-comment`)

  const [hasInitialised, setHasInitialised] = useState(false)
  const [showDefault, setShowDefault] = useState(false)
  const [remainingLife, setRemainingLife] = useState(undefined)
  const [inputState, setInputState] = useState({
    default: undefined,
    disabled: true,
    maxAllowed: 0,
    value: undefined
  })
  const [comment, setComment] = useState(currentLifecycleInterventionYearComment)

  useEffect(() => {
    if (currentLifecycleInterventionYear) {
      const interventionYear = new Date(currentLifecycleInterventionYear).getFullYear().toString()

      updateComponentState(interventionYear)
    }
  
    if (installDate && !currentLifecycleInterventionYear) {
      const lifecycleEndYear = getLifecycleEndYear()
      
      form.setFieldsValue({ [inputKey]: lifecycleEndYear.toString() })

      updateComponentState(lifecycleEndYear.toString())
    }

    setHasInitialised(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (installDate) {
      handleInstallDateChange()
    } else {
      setShowDefault(false)
      setRemainingLife(undefined)
      setInputState(prev => ({
        ...prev,
        disabled: true,
        value: undefined
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ installDate ])

  const getLifecycleEndYear = () => {
    const installedDate = new Date(installDate).getFullYear()
    const lifecyclePeriod = assetType?.lifecycle || DEFAULT_LIFECYCLE
    
    return installedDate + lifecyclePeriod
  }

  function updateRemainingLife (selectedYear) {
    const currentYear = new Date().getFullYear()

    const remainingLifeYears =  selectedYear - currentYear > 0 ? selectedYear - currentYear : 0

    setRemainingLife(remainingLifeYears)
  }

  function handleInstallDateChange() {
    if (installDate && hasInitialised) {
      const lifecycleEndYear = getLifecycleEndYear()
      
      form.setFieldsValue({ [inputKey]: lifecycleEndYear.toString() })

      updateRemainingLife(lifecycleEndYear)
      updateComponentState(lifecycleEndYear.toString())

      forceUpdate()
    }
  }

  function updateComponentState(interventionDate) {
    const lifecycleEndYear = getLifecycleEndYear()

    updateRemainingLife(interventionDate)

    setInputState(prev => ({
      ...prev,
      disabled: false,
      default: moment(lifecycleEndYear, DATE_FORMAT),
      maxAllowed: lifecycleEndYear + MAX_INTERVENTION_PERIOD_BEYOND_EOL,
      value: moment(interventionDate, DATE_FORMAT)
    }))

    setShowDefault(true)
  }
  
  return (
    <LifecycleInterventionInputStyled>
      <div className="date-field-wrapper">
        <DatePicker
          disabled={inputState?.disabled}
          disabledDate={(currentDate) => {
            const lowerDateRange = installDate?.toDate()
            const upperDateRange = new Date(`01 January ${inputState?.maxAllowed} 00:00:00 UTC`)

            return currentDate < lowerDateRange || currentDate > upperDateRange
          }}
          getPopupContainer={(trigger) => trigger.parentNode}
          picker="year"
          value={inputState?.value}
          onChange={(_date, dateString) => {
            if (dateString !== '') {
              form.setFieldsValue({ [inputKey]: dateString })

              const selectedDateObject = moment(dateString, DATE_FORMAT)

              updateRemainingLife(selectedDateObject.toDate()?.getFullYear())

              setInputState(prev => ({
                ...prev,
                value: selectedDateObject
              }))
            } else {
              form.setFieldsValue({ [inputKey]: inputState?.default?.toDate()?.getFullYear().toString() })

              updateRemainingLife(inputState?.default?.toDate()?.getFullYear())

              setInputState(prev => ({
                ...prev,
                value: prev.default
              }))
            }

            forceUpdate()
          }}
        />
        {showDefault && (
          <div className="default-label">
            (default&nbsp;<span>{inputState?.default?.toDate()?.getFullYear()}</span>)
          </div>
        )}
        {remainingLife !== undefined && (
          <div className="remaining-life-label">Asset remaining life is {remainingLife} {remainingLife !== 1 ? 'years' : 'year'}</div>
        )}
      </div>
      <div className="notes-field-wrapper">
        <label className="lifecycle-intervention-comment-label">
          <span>Lifecycle Intervention Comment</span>
          <Tooltip
            title={HELP_TEXT}
            placement="left"
          >
            <div className="form-helper">{/* the div around the Icon triggers the tooltip on hover */}
              <QuestionCircleIcon />
            </div>
          </Tooltip>
        </label>
        <Input.TextArea
          className="lifecycle-intervention-comment"
          disabled={inputState.disabled}
          value={comment}
          onChange={(e) => {
            const note = e.target.value
            
            form.setFieldsValue({ 'facets_lifecycleInterventionYearComment': note})

            setComment(note)
            forceUpdate()
          }}
        />
      </div>
    </LifecycleInterventionInputStyled>
  )
}

export default LifecycleInterventionInput
